import { faScissors } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox } from 'primereact/checkbox'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransformedStatsData } from '../util/useEstablishmentStatsQuery'

export const SquareWrapper = styled.div<{
  hoverColor?: string
  isAdmin: boolean
}>`
  background-color: rgba(255, 255, 255, 0.44);
  border-radius: 30px;
  text-transform: uppercase;
  padding: 1.5em 0;
  transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
  transition-duration: 0.65s;
  font-weight: 800;
  font-style: normal;
  letter-spacing: 0.16em;
  line-height: 1.1em;
  text-align: center;
  color: white;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: ${props => (props.isAdmin ? 'pointer' : 'default')};
  width: 300px;
  height: 300px; // Made equal to width for perfect squares
  &:hover {
    color: ${props => props.hoverColor || 'white'};
  }
`

const CheckBoxWrapper = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
`

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1.2;
  margin-bottom: 12px;
  width: 100%;
  height: 34px; // This allows for approximately 2 lines of text
  text-overflow: ellipsis;
  transition: all 0.2s ease;
  padding: 0 16px;
`

const Value = styled.p`
  font-size: 32px;
  font-weight: bold;
  transition: all 0.2s ease;
`

const Circle = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
`

const StyledIcon = styled(FontAwesomeIcon)`
  transition: all 0.2s ease;
`

const Note = styled.p`
  font-size: 10px;
  font-weight: 300;
  line-height: 1.2;
  margin-top: 12px;
  width: 100%;
  height: 34px; // This allows for approximately 2 lines of text
  text-overflow: ellipsis;
  transition: all 0.2s ease;
  padding: 0 16px;
  position: absolute;
  bottom: 0;
`

type AdminSquareProps = {
  toggleFeature: (state: TransformedStatsData) => void
  setShowLoaderOverlay: (state: boolean) => void
}

type Props = {
  data: TransformedStatsData
  refData: TransformedStatsData[]
  adminSquareProps?: AdminSquareProps
}

export const StatSquare: React.FC<Props> = ({
  data,
  refData,
  adminSquareProps,
}) => {
  const { t } = useTranslation()

  const getValue = () => {
    const versusValue = data.meta?.percentageVersus
      ? refData.find(foundData => foundData.key === data.meta?.percentageVersus)
      : null

    return versusValue?.value && data.value !== null && data.value !== undefined
      ? `${Math.round((data.value / versusValue.value) * 100)}%`
      : data.value
  }

  return (
    <SquareWrapper
      isAdmin={!!adminSquareProps}
      hoverColor={data.meta?.color}
      onClick={() => {
        if (adminSquareProps) {
          const { toggleFeature } = adminSquareProps
          toggleFeature(data)
        }
      }}>
      <Circle>
        <StyledIcon icon={data.meta?.FaIcon || faScissors} size="2x" />
      </Circle>
      <Title>
        {data.meta?.title ? t(data.meta.title) : t(data.key.replace(/_/g, ' '))}
      </Title>
      <Value>{getValue()}</Value>
      {data.meta?.note ? <Note>{`* ${t(data.meta.note)}`}</Note> : null}
      <CheckBoxWrapper>
        {!!adminSquareProps && <Checkbox checked={data.isPublic} />}
      </CheckBoxWrapper>
    </SquareWrapper>
  )
}
